import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { finalize, take } from 'rxjs/operators';

import { ErrorHelperService } from '@core/helper/error-helper.service';

import { UserService } from '@core/api/user.service';
import * as fromApp from '@store';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { RegexConstants } from '@shared/constants/regex.constants';
import { RoleEnum } from '@shared/constants/role.enums';
import { User } from '@shared/models';
import { SpinnerService } from '@shared/spinner/spinner.service';

@Component({
  selector: 'fc-add-user-dialog',
  templateUrl: './add-user-dialog.component.html',
  styleUrls: ['./add-user-dialog.component.scss'],
  standalone: false
})
export class AddUserDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('email') emailInputElement: ElementRef<HTMLInputElement>;

  destroyRef = inject(DestroyRef);

  errorMessage: string;
  isSuperAdmin: boolean;
  userForm: UntypedFormGroup;
  emailErrorMessage: string = '';
  RoleEnum = RoleEnum;
  title: string = 'Invite User';
  primaryButtonText: string = 'INVITE';
  adminRoles: RoleEnum[] = [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.FinanceAdmin];
  disableUserRoleOptions: boolean = false;

  constructor(
    readonly dialogRef: MatDialogRef<AddUserDialogComponent>,
    readonly formBuilder: UntypedFormBuilder,
    readonly userService: UserService,
    readonly store: Store<fromApp.AppState>,
    readonly errorHelperService: ErrorHelperService,
    readonly cdr: ChangeDetectorRef,
    readonly spinnerService: SpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: User
  ) {}

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.isSuperAdmin = currentUser.isSuperAdmin;
      });

    const roles = this.data?.role?.name;

    this.userForm = this.formBuilder.group({
      email: [
        this.data?.email || null,
        [Validators.maxLength(254), Validators.pattern(RegexConstants.EMAIL), Validators.required],
      ],
      role: [roles || RoleEnum.Landowner, Validators.required],
      isActive: [true, Validators.required],
    });

    // set data when in edit mode
    if (roles) {
      if (this.adminRoles.includes(this.data?.role.name as RoleEnum)) {
        // disable the user role options if the user is an admin
        this.disableUserRoleOptions = true;
      } else {
        // disable the role field if the user is not a admin
        this.userForm.get('role').disable();
      }
    }

    if (this.data.id) {
      this.title = 'Edit User Permissions';
      this.userForm.get('email').disable();
      this.primaryButtonText = 'UPDATE USER';
      this.userForm.get('isActive').setValue(this.data.isActive);
    }

    this.userForm
      .get('email')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.emailErrorMessage = this.errorHelperService.getEmailErrorMessage(this.userForm.controls.email);

        if (this.errorMessage) {
          // if the email is updated, remove the error message until they submit again
          this.errorMessage = '';
          this.userForm.get('email').setErrors({ notUnique: false });
        }
      });
  }

  ngAfterViewInit() {
    // Focus on the email input when the dialog is opened and tell angular to check for changes
    this.emailInputElement.nativeElement.focus();
    this.cdr.detectChanges();
  }

  submitUser(): void {
    this.spinnerService.show('app-spinner');
    const userData = this.userForm.getRawValue();
    this.userService
      .inviteUpdateUser(userData.email.toLowerCase(), userData.role, userData.isActive, this.data?.id)
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe({
        next: (user: User) => {
          this.dialogRef.close(user);
        },
        error: error => {
          if (error.status === 400) {
            // need to set the state of the control to invalid to display the error message
            this.errorMessage = error.error.detail;
            this.userForm.get('email').setErrors({ notUnique: true });
          }
        }
      });
  }
}
